<template>
  <div class="history">
    <!-- Header -->
    <div class="history__header">
      <a
        class="history__header-close"
        @click="$router.back()"
      >
        <UseSvg
          class="icon"
          name="close"
        />
      </a>
      <div class="history__header-details">
        <p class="typo-ignore typo-title">
          <strong>{{ $t('issues.history') }}</strong>
        </p>
        <div
          aria-label="issue-id"
          class="typo-small"
        >
          {{ `${$t('issues.issue')} #${shortId}` }}
        </div>
      </div>
    </div>

    <!-- Content structure -->
    <div class="history__content">
      <div
        v-for="history in issue.versions"
        :key="history.id"
        :class="{ 'history__content-item': !history.object_changes?.location_id }"
      >
        <!-- Ignore all location_id changes -->
        <template v-if="!history.object_changes?.location_id">
          <p class="history__content-item-date typo-ignore typo-small-title">
            {{ formatDate(history.object_changes.updated_at[1]) }}
          </p>

          <div class="history__content-item-detail">
            <template
              v-for="(value, name) in history.object_changes"
              :key="name"
            >
              <div
                v-if="showDetail(name, history)"
                :class="`type-${name}`"
              >
                <div
                  v-if="name !== 'updated_at' && name !== 'direct_region_id' && history.event !== 'create'"
                >
                  <p class="typo-ignore typo-small-title">
                    <strong>{{ headerTitle(name, history) }}</strong>
                    {{ $t(`issues.was`) }} {{ $t(`issues.${history.event}`) }}
                    {{ $t(`issues.by`) }} {{ history.user.full_name }}
                  </p>
                  <div :class="contentItemClass(name)">
                    <div class="history__content-comparison">
                      <div :class="`history__content-comparison--${itemStructure(name)}`">
                        <div
                          v-if="name === 'location'"
                          class="history__content-comparison-map"
                        >
                          <IssueLocationInput
                            v-model="value[0]"
                            :canEdit="false"
                            :diagram="operationDiagram"
                            :regions="[value[0]]"
                            :riskRatingLevel="getRiskRatingLevel(history.object.risk_rating)"
                          />
                        </div>
                        <!-- Handles object data e.g {x: "0", y: "0", z: "0"} -->
                        <template v-else-if="!!value[0] && typeof value[0] === 'object'">
                          <p
                            v-for="[index, [key, objectValue]] of Object.entries(Object.entries(value[0]))"
                            :key="key"
                            :class="`typo-ignore typo-small ${itemRatingClasses(name, key, history)}`"
                          >
                            {{ itemGroup(index, key, objectValue, value[0]) }}
                          </p>
                        </template>
                        <p
                          v-else
                          :class="`typo-ignore typo-small ${itemRatingClasses(name, value[0], history)}`"
                        >
                          {{ itemName(history) }}
                          <UseSvg
                            v-if="value[0] !== 'none'"
                            :class="{ 'icon': true, 'icon--margin-left': itemName(history) }"
                            :name="`${renderIcon(name, history)}`"
                          />
                          {{ itemContent(name, value[0], history.user.full_name) }}
                        </p>
                      </div>

                      <div class="history__content-comparison-divider">
                        <ArrowWithStemSvg class="icon" />
                      </div>

                      <div :class="`history__content-comparison--${itemStructure(name)}`">
                        <div
                          v-if="name === 'location'"
                          class="history__content-comparison-map"
                        >
                          <IssueLocationInput
                            v-model="value[1]"
                            :canEdit="false"
                            :diagram="operationDiagram"
                            :regions="[value[1]]"
                            :riskRatingLevel="getRiskRatingLevel(history.object.risk_rating)"
                          />
                        </div>
                        <!-- Handles object data e.g {x: "0", y: "0", z: "0"} -->
                        <template v-else-if="!!value[1] && typeof value[1] === 'object'">
                          <p
                            v-for="[index, [key, objectValue]] of Object.entries(Object.entries(value[1]))"
                            :key="key"
                            :class="`typo-ignore typo-small ${itemRatingClasses(name, key, history)}`"
                          >
                            {{ itemGroup(index, key, objectValue, value[1]) }}
                          </p>
                        </template>
                        <p
                          v-else
                          :class="`typo-ignore typo-small ${itemRatingClasses(name, value[1], history)}`"
                        >
                          {{ itemName(history) }}
                          <UseSvg
                            v-if="value[1] !== 'none'"
                            :class="{ 'icon': true, 'icon--margin-left': itemName(history) }"
                            :name="`${renderIcon(name, history)}`"
                          />
                          {{ itemContent(name, value[1], history.user.full_name) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- Created item -->
            <div v-if="history.event === 'create'">
              <p>
                <strong>{{ $t('issues.issue') }} {{ $t(`issues.${history.event}`) }}</strong>
                {{ $t(`issues.by`) }} {{ history.user.full_name }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import I18n from 'i18n-js';

import { renderShortId } from '@/functions/utils.js';
import { formatDate } from '@/functions/date_format_helpers';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';
import useAjax from '@/functions/use_ajax';
import useOrganisationProfile from '@/functions/use_organisation_profile';

import IssueLocationInput from '@/components/issues/issue_location_input.vue';

import UseSvg from '@/components/generic/use_svg.vue';
import ArrowWithStemSvg from '@/images/arrow-with-stem.svg';

const props = defineProps({
  historyURL: { type: String, required: true },
  issueId: { type: String, required: true },
  currentOperation: { type: Object, required: false, default: null },
});

const ajax = useAjax();
const { isAga, priorityLabel } = useOrganisationProfile();

const issue = ref({});
const shortId = computed(() => renderShortId(props.issueId));
const operationDiagram = computed(() => props.currentOperation?.diagram_identifier);

onMounted(() => {
  fetchIssue();
});

/**
 * Fetches the issue and history
 */
async function fetchIssue() {
  const { ok, data } = await ajax(props.historyURL);
  if (ok) {
    issue.value = data;
  }
}

// returns a modifier class name for styling
function itemStructure(name) {
  switch (name) {
    case 'description':
      return 'text';
    case 'location_description':
      return 'text';
    case 'recommendation':
      return 'text';
    case 'title':
      return 'text';
    case 'location':
      return 'text';
    default:
      return 'default';
  }
}

function itemName(data) {
  const showName = data?.item_type.includes('DeteriorationMechanismRating');
  return showName && data?.object?.name && I18n.t(`deterioration_mechanisms.${data.object.name}.name`) || '';
}

function itemContent(name, value, user) {
  switch (name) {
    case 'status':
      return I18n.t(`issues.${value}`);
    case 'requires_task':
      return I18n.t(`issues.${value}`);
    case 'element_class':
      return I18n.t(`element_classes.${value}.name`);
    case 'element_material':
      return I18n.t(`element_materials.${value}`);
    case 'closed_at':
      return value === null ? I18n.t('general.none') : formatDate(value);
    case 'closed_by_id':
      return user;
    case 'priority':
      return value === 'none' || value === null
        ? I18n.t('general.none')
        : priorityLabel(value);
    case 'position':
      return `x: ${fixDecimal(value.x, 3)}, y: ${fixDecimal(value.y, 3)}, z: ${fixDecimal(value.z, 3)}`;
    default:
      return value === 'none' || value === null || value.length === 0
        ? I18n.t('general.none')
        : value;
  }
}

function isDetmech(history) {
  return history?.item_type.includes('DeteriorationMechanismRating');
}

function itemRatingClasses(name, value, history) {
  if (isDetmech(history)) return `overall_condition_rating overall_condition_rating--${value}`;

  switch (name) {
    case 'description':
      return '';
    case 'location_description':
      return '';
    case 'recommendation':
      return '';
    case 'title':
      return '';
    case 'risk_rating':
      return `${name} ${name}--${getRiskRatingLevel(value)}`;
    default:
      return `${name} ${name}--${value}`;
  }
}

function renderIcon(name, history) {
  if (isDetmech(history)) return 'rating_square';

  switch (name) {
    case 'priority':
      return 'rating_triangle';
    case 'overall_condition_rating':
      return 'rating_square';
    case 'risk_rating':
      return 'rating_circle';
    default:
      return '';
  }
}

function headerTitle(key, history) {
  if (isDetmech(history)) return I18n.t('issues.deterioration_mechanisms');

  switch (key) {
    case 'position':
    case 'set_number':
    case 'shaft_element_set_id':
      return I18n.t('issues.location');
    default:
      return I18n.t(`issues.${key}`);
  }
}

function showDetail(key, history) {
  /**
   * Risk ratings are hidden for organisations on the AGA profile
   */
  if (key === 'risk_rating' && isAga.value) return false;

  /**
   * If we have shaft_element_set_id we should only show it's data in the location section
   */
  const hasKey = Object.prototype.hasOwnProperty.call(history.object_changes, 'shaft_element_set_id');
  if (hasKey) return key === 'shaft_element_set_id';
  return true;
}

/**
 * Prepare the structure for the position data and only add a comma
 * to all instances except the last index
 * @param { Number } index
 * @param { String } key
 * @param { Number } value
 * @param { { key: value } } object
 */
function itemGroup(index, key, value, object) {
  const addComma = index < Object.entries(object).length - 1 ? ',' : '';
  return `${ key }: ${ fixDecimal(value, 8) }${ addComma }`;
}

/**
 * Check if the value is a longer decimal than the given decimal point.
 * If it is longer we reduce it to the given decimalPoint,
 * else we keep the value as is and not add unnecessary zeros
 * @param { Number } value
 * @param { Number } decimalPoint
 */
function fixDecimal(value, decimalPoint) {
  if (!value) return;
  const isDecimal = value % 1 != 0;
  const isLonger = isDecimal && decimalPoint && value.toString().length >= decimalPoint;
  return isLonger ? Number(value).toFixed(decimalPoint) : value;
}

/**
 * Add padding on location wrapper
 * @param { String } name
 */
function contentItemClass(name) {
  const addPadding = name === 'location' ? 'history__content-item-detail-info--padding-x': '';
  return `history__content-item-detail-info history__content-item-detail-info--${itemStructure(name)} ${addPadding}`;
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

$history-content-width: rem(992);
$history-date-width: rem(220);

.history {
  background-color: map-get($background-color, darkest);
  min-height: 100vh;

  &__header {
    max-width: $history-content-width;
    margin: 0 auto;
    position: relative;
    padding: bu(3) bu(2);

    &-close {
      position: absolute;
      top: 0;
      bottom: 0;
      display: flex;
      align-items: center;

      svg {
        cursor: pointer;
      }
    }

    &-details {
      flex: 1;
      text-align: center;

      & div {
        margin-top: bu(.5);
      }
    }
  }

  &__content {
    max-width: $history-content-width;
    margin: 0 auto;
    background-color: map-get($background-color, darker);

    &-item {
      border-bottom: solid 2px map-get($background-color, darkest);

      @include breakpoint(medium) {
        display: flex;
        padding: bu(3) bu(5) 0 bu(5);
      }

      &:last-of-type {
        padding-bottom: bu(20);
      }

      p {
        padding: bu(1.5) bu(2.5) 0 bu(2.5);

        @include breakpoint(medium) {
          padding: 0;
        }
      }
    }

    &-item-date {
      color: map-get($foreground-color, dark);

      @include breakpoint(medium) {
        min-width: $history-date-width;
      }
    }

    &-item-detail {
      width: 100%;
    }

    &-item-detail-info {
      background-color: map-get($background-color, darkest);
      margin-top: bu(3);

      @include breakpoint(medium) {
        border-radius: map-get($border-radius, heavier);
        margin-bottom: bu(3);

        &--padding-x {
          padding: 0 bu(2);
        }
      }

      &--default {
        width: 100%;
        font-weight: 700;

        @include breakpoint(medium) {
          width: auto;
          display: inline-flex;
        }
      }
    }

    &-comparison {
      display: flex;
      padding: bu(2.5);

      &--text {
        flex: 1;

        p {
          align-items: flex-start;
        }
      }

      &--default {
        border-radius: map-get($border-radius, medium);
        background-color: map-get($background-color, darker);
        width: rem(160);
        padding: bu(2) bu(1);
        text-align: center;

        p {
          padding: 0;
          display: flex;
          justify-content: center;
          align-items: center;

          .icon {
            width: bu(1.75);
            height: bu(1.75);
            margin-right: bu(1.5);

            &--margin-left {
              margin-left: bu(1.5);
            }
          }
        }
      }

      &-divider {
        width: 1px;
        background-color: map-get($background-color, darker);
        margin: 0 bu(4.5);
        position: relative;

        .icon {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          background-color: map-get($background-color, darkest);
        }
      }

      &-map {
        // Because we scoped the styles I don't think the styles in this group are being reached
        .issue__map-preview {
          margin: 0;
          border-radius: 0;
        }

        canvas {
          border-radius: map-get($border-radius, heavier);
        }

        .issue__map-preview-info {
          flex-direction: column;
          background-color: map-get($background-color, darkest);
          padding-right: 0;
          padding-bottom: 0;
          padding-left: 0;

          &-coord {
            color: map-get($foreground-color, dark);

            span {
              color: currentColor;
              margin-left: 0;

              @include breakpoint(medium) {
                margin-left: bu(.5);
              }
            }
            &:last-child {
              margin-left: 0;
              margin-top: bu(.5);
            }
          }
        }
      }
    }
  }
}

.status {
  &--resolved {
    color: map-get($foreground-color, dark);
  }
  &--open_issue {
    color: map-get($important-color, light);
  }
}

.closed_at {
  &--null {
    color: map-get($foreground-color, dark);
  }
}

.requires_task {
  &--false {
    color: map-get($foreground-color, dark);
  }
  &--true {
    color: map-get($important-color, light);
  }
}

.priority {
  @each $priority, $color in $priority-color {
    &--#{$priority} {
      .icon {
        color: map-get($color, default);
      }
    }
  }

  &--none {
    color: map-get($foreground-color, dark);
  }
}

.overall_condition_rating {
  @each $condition-rating, $color in $condition-rating-color {
    &--#{$condition-rating} {
      .icon {
        color: map-get($color, default);
      }
    }
  }

  &--none {
    color: map-get($foreground-color, dark);
  }
}

.risk_rating {
  @each $risk-rating, $color in $risk-rating-color {
    &--#{$risk-rating} {
      .icon {
        color: map-get($color, default);
      }
    }
  }

  &--none {
    color: map-get($foreground-color, dark);
  }
}


</style>
